import VideoModule from "./videoModule";
import gsap from "gsap";
import Header from "./header";
import Animations from "./animations";

const Accordion = (() => {

    const SELECTOR_MODULE = '.accordion';
    const SELECTOR_ITEM = '.accordion__item';
    const SELECTOR_BUTTON_OPEN = '.btn-open-item';

    const initialize = () => {

        const modules = document.querySelectorAll(SELECTOR_MODULE);

        if (!modules?.length) return;

        modules.forEach((accordion) => {

            initModule(accordion);

            const buttons = accordion.querySelectorAll(SELECTOR_BUTTON_OPEN);
            buttons.forEach(btn => {

                btn.addEventListener('click', ev => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    selectItem(accordion, btn)
                })
            });


            // Attach API
            accordion.api = AccordionAPI(accordion);
        });


    };


    const initModule = accordion => {


        const items = accordion.querySelectorAll(SELECTOR_ITEM);
        const videoModules = [];

        items.forEach(item => {

            const el = item.querySelector(VideoModule.SELECTOR_MODULE);

            if (!el) return;

            const vm = VideoModule.initialize(el)
            videoModules.push(vm);

            initItemAnimations(item, vm);
        });

        let videosLoading = false;
        let videosLoaded = false;
        let animationPlayed = false;

        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: accordion,
                start: () => `top top+=${Header.getCurrentHeight()}`,
                end: () => "+=" + window.innerHeight * 2,
                pin: true,
                markers: false,
                invalidateOnRefresh: true,

                onToggle: self => {
                    if (self.isActive) {
                        if (!animationPlayed && !videosLoading) {
                            videosLoading = true;

                            loadVideos(videoModules)
                                .then(() => accordion.api.playSequentially({
                                    beforePlay: 1000,
                                    afterVideo: 1000
                                }))
                                .then(() => animationPlayed = true)
                                .finally(() => videosLoading = false);
                        }
                    } else {
                        accordion.api.cancelSequential();
                    }
                },
                /* onEnter: () => {

                    // ensure playing animation only once
                    if (videosLoading || videosLoaded || animationPlayed) return;

                    videosLoading = true;

                    // load video
                    loadVideos(videoModules)
                        .then(() => {

                            videosLoaded = true;
                            console.debug('all videos loaded');

                            accordion.api.playSequentially({
                                beforePlay: 1000,   // let text animation settle
                                afterVideo: 1000,
                                gap: 0 
                            })
                                .then(() => {
                                    animationPlayed = true;
                                })
                                .catch((err) => console.error(err));

                        })
                        .catch((err) => {
                            console.error(err);
                        })
                        .finally(() => {
                            videosLoading = false;
                        });

                },
                onLeave: () => {
                    accordion.api.cancelSequential();
                } */

            }
        });

        timeline.add(Animations.revealElements(items, {}, {
            duration: .5,
            stagger: 0.3
        }));




    };

    const initItemAnimations = (item, itemVideoModule) => {

        const animated = item.querySelectorAll('.content__text');

        itemVideoModule.video?.addEventListener("ended", () => handleVideoEnded(animated, itemVideoModule));
        itemVideoModule.video?.addEventListener("play", () => handleVideoPlay(animated, itemVideoModule));

        // initial reveal of active items elements
        if (item.classList.contains('active')) {
            Animations.revealElements(animated);
        }

    }


    // handler function revealing text elements when video ends
    const handleVideoEnded = (elements, videoModule) => {
        /* revealElements(elements); */
        videoModule.revealPoster();
    };

    // handle case when user hits the play btn
    const handleVideoPlay = (elements, videoModule) => {

        gsap.timeline()
            /* .add(hideElements(elements)) */
            .to(videoModule.controls, { autoAlpha: 1, duration: 0.5 }, "<")
            .to(videoModule.poster, { autoAlpha: 0, duration: 0.5 }, "<");

    };




    const loadVideos = async (videoModules) => {

        return Promise.all(
            videoModules.map((vm) =>
                vm.load()
            )
        );
    }

    /**
     * 
     * @param {*} accordion 
     * @param {*} currentButton the button selected
     * @param {*} options force: perform selection even item is active (for sequential play), playVideo: control for starting video in case of sequential animation play is controlled by the animation function. 
     * @returns 
     */
    const selectItem = (accordion, currentButton, options = {}) => {

        // needed for programmatically start the animation on the first already active element
        const {
            force = false,
            playVideo = true
        } = options;

        const videoElements = accordion.querySelectorAll(`${VideoModule.SELECTOR_MODULE}`);
        const items = accordion.querySelectorAll(SELECTOR_ITEM);

        const currentItem = currentButton.closest(SELECTOR_ITEM);


        const isAlreadyActive = currentItem.classList.contains("active");
        if (isAlreadyActive && !force) return;

        // stop/reset videos
        resetVideos(videoElements);

        items.forEach(item => {

            const btn = item.querySelector(SELECTOR_BUTTON_OPEN);
            const animated = item.querySelector('.content__text');

            const activate = item === currentItem;

            item.classList.toggle("active", activate);
            btn.setAttribute("aria-expanded", activate);

            if (activate) {
                Animations.revealElements(animated, { y: 0 }, { duration: 1, delay: .5 })
            } else {
                Animations.hideElements(animated, {}, { duration: 0, ease: 'none' })
            }



        });

        if (playVideo) {

            const videoEl = currentItem.querySelector(".video__module");

            const vm = VideoModule.getInstance(videoEl);
            vm?.play();
        }

    }

    const resetVideos = (videoElements) => {

        videoElements.forEach(el => {

            const vm = VideoModule.getInstance(el);

            vm.reset();
            vm.revealPoster();
        });
    };

    const AccordionAPI = (accordion) => {
        const buttons = accordion.querySelectorAll(SELECTOR_BUTTON_OPEN);
        const items = accordion.querySelectorAll(SELECTOR_ITEM);

        // helper var for cancelling sequential play
        let runToken = 0;

        return {
            cancelSequential() {
                runToken++;
            },
            open(indexOrButton, { playVideo = false, force = true } = {}) {
                let btn;
                if (typeof indexOrButton === 'number') {
                    btn = buttons[indexOrButton];
                } else {
                    btn = indexOrButton;
                }
                if (!btn) return;
                selectItem(accordion, btn, { playVideo, force });
            },

            next({ playVideo = true } = {}) {
                const activeIndex = [...items].findIndex(item => item.classList.contains('active'));
                const nextIndex = (activeIndex + 1) % items.length;
                this.open(nextIndex, { playVideo, force: true });
            },

            prev({ playVideo = true } = {}) {
                const activeIndex = [...items].findIndex(item => item.classList.contains('active'));
                const prevIndex = (activeIndex - 1 + items.length) % items.length;
                this.open(prevIndex, { playVideo, force: true });
            },
            wait(ms = 0) {

                return new Promise(resolve => setTimeout(resolve, ms))
            },

            async playSequentially({
                gap = 0,           // delay between items
                beforePlay = 0,    // delay after item opens, before video starts
                afterVideo = 0     // delay after video ends
            } = {}) {

                const token = ++runToken;

                for (let i = 0; i < buttons.length; i++) {

                    if (token !== runToken) return;

                    this.open(i, { playVideo: false, force: true });

                    if (beforePlay) {
                        await this.wait(beforePlay);
                        if (token !== runToken) return;

                    }

                    const videoEl = buttons[i].closest(SELECTOR_ITEM).querySelector('.video__module');


                    if (videoEl) {
                        const vm = VideoModule.getInstance(videoEl);
                        await new Promise(resolve => {
                            const onEnd = () => {
                                vm.video.removeEventListener('ended', onEnd);
                                resolve();
                            };
                            vm.video.addEventListener('ended', onEnd);
                            vm.play();
                        });

                        if (token !== runToken) return;

                        if (afterVideo) {
                            await this.wait(afterVideo);
                            if (token !== runToken) return;
                        }
                    } else {
                        await this.wait(gap);
                        if (token !== runToken) return;
                    }

                    await this.wait(gap);
                    if (token !== runToken) return;
                }
            },

            getCurrentIndex() {
                return [...items].findIndex(item => item.classList.contains('active'));
            }
        };
    };




    return {
        initialize
    }

})();

export default Accordion;

